<template>
  <div>
    <!-- Trigger Button -->
    <b-button
      v-if="$store.state.app.permissions.includes('show-audit-logs')"
      variant="outline-info"
      size="sm"
      pill
      class="audit-log-button"
      @click="openDrawer"
    >
      {{ $t("audit.action-monitor") || "مراقب الإجراءات" }}
    </b-button>

    <!-- Drawer (Modal styled as drawer) -->
    <b-modal
      :id="drawerId"
      ref="auditLogDrawer"
      hide-footer
      no-close-on-backdrop
      body-class="p-0"
      header-class="audit-drawer-header"
      modal-class="audit-drawer-modal"
      @hidden="resetDrawer"
    >
      <template #default>
        <!-- Loading State -->
        <div
          v-if="loading"
          class="text-center py-5"
        >
          <b-spinner variant="primary" />
          <p class="mt-2">
            {{ $t("common.loading") || "جاري التحميل..." }}
          </p>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!loading && auditLogs.length === 0"
          class="text-center py-5"
        >
          <feather-icon
            icon="FileTextIcon"
            size="48"
            class="text-muted mb-2"
          />
          <p class="text-muted">
            {{ $t("audit.no-audit-logs") || "لا توجد سجلات تدقيق" }}
          </p>
        </div>

        <!-- Audit Logs Timeline -->
        <div
          v-else
          class="audit-logs-container"
        >
          <div
            v-for="(audit, index) in auditLogs"
            :key="audit.id || index"
            class="audit-log-item"
          >
            <!-- Event and Date -->
            <div class="audit-log-header">
              <b-badge
                :variant="getEventVariant(audit.event)"
                class="event-badge"
              >
                {{ getEventLabel(audit.event) }}
              </b-badge>
              <span class="audit-date">
                {{ formatDateTime(audit.created_at) }}
              </span>
            </div>

            <!-- User Info -->
            <div class="audit-log-user">
              <feather-icon
                icon="UserIcon"
                size="16"
                class="mr-1"
              />
              <strong>{{ $t("audit.user") || "المستخدم" }}:</strong>
              <span>{{ getUserName(audit) }}</span>
            </div>

            <!-- Model Info -->
            <div class="audit-log-model">
              <feather-icon
                icon="DatabaseIcon"
                size="16"
                class="mr-1"
              />
              <strong>{{ $t("audit.model") || "النموذج" }}:</strong>
              <span>{{ getModelName(audit.auditable_type) }}</span>
            </div>

            <!-- Changes -->
            <div
              v-if="hasChanges(audit)"
              class="audit-log-changes"
            >
              <h6 class="mb-2">
                <feather-icon
                  icon="EditIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("audit.changes") || "التغييرات" }}
              </h6>
              <div class="changes-list">
                <div
                  v-for="changeItem in getChangesList(audit)"
                  :key="changeItem.key"
                  class="change-item"
                >
                  <div class="change-field">
                    <strong>{{ formatFieldName(changeItem.key) }}:</strong>
                  </div>
                  <div class="change-values">
                    <div class="change-value-container">
                      <span class="old-value">
                        {{ changeItem.oldValue }}
                      </span>
                      <span class="arrow">→</span>
                      <span class="new-value">
                        {{ changeItem.newValue }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Divider -->
            <hr
              v-if="shouldShowDivider(index)"
              class="my-3"
            >
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BModal,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'AuditLogDrawer',
  components: {
    BButton,
    BModal,
    BSpinner,
    BBadge,
    ToastificationContent,
  },
  props: {
    modelType: String,
    modelId: String,
    auditId: String,
  },
  data() {
    return {
      loading: false,
      auditLogs: [],
      error: null,
      drawerId: `audit-log-drawer-${Math.random().toString(36).substr(2, 9)}`,
    }
  },
  methods: {
    openDrawer() {
      this.$bvModal.show(this.drawerId)
      this.fetchAuditLogs()
    },
    resetDrawer() {
      this.auditLogs = []
      this.error = null
    },
    async fetchAuditLogs() {
      this.loading = true
      this.error = null

      try {
        let url = ''

        if (this.auditId) {
          url = `/audits/${this.auditId}`
          const response = await this.$http.get(url)
          this.auditLogs = [response.data.data || response.data]
        } else if (this.modelType) {
          url = `/audits?auditable_type=${encodeURIComponent(this.modelType)}`
          if (this.modelId) {
            url += `&auditable_id=${encodeURIComponent(this.modelId)}`
          }
          const response = await this.$http.get(url)
          this.auditLogs = Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : []
        } else {
          this.error = 'No valid parameters provided'
          this.loading = false
          return
        }

        if (this.auditLogs.length > 1) {
          this.auditLogs.sort(this.sortAuditLogs)
        }
      } catch (error) {
        console.error('Error fetching audit logs:', error)
        this.error = this.getErrorMessage(error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.error,
          },
        })
        this.auditLogs = []
      } finally {
        this.loading = false
      }
    },
    getEventVariant(event) {
      const variants = { created: 'success', updated: 'warning', deleted: 'danger' }
      return variants[event] || 'secondary'
    },
    getEventLabel(event) {
      const labels = {
        created: this.$t('audit.created') || 'تم الإنشاء',
        updated: this.$t('audit.updated') || 'تم التحديث',
        deleted: this.$t('audit.deleted') || 'تم الحذف',
      }
      return labels[event] || event
    },
    getModelName(modelType) {
      if (!modelType) return 'N/A'
      const parts = modelType.split('\\')
      return parts[parts.length - 1] || modelType
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString('ar-IQ', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatFieldName(key) {
      let formatted = key.replace(/_/g, ' ')
      formatted = formatted.replace(/([A-Z])/g, ' $1')
      return formatted.replace(/\b\w/g, l => l.toUpperCase()).trim()
    },
    formatValue(value) {
      if (value === null || value === undefined) return 'N/A'
      if (typeof value === 'object') return JSON.stringify(value, null, 2)
      return String(value)
    },
    getUserName(audit) {
      if (audit && audit.user && audit.user.name) return audit.user.name
      return 'N/A'
    },
    hasChanges(audit) {
      return (audit && audit.old_values) || (audit && audit.new_values)
    },
    getChangesList(audit) {
      if (!audit || !audit.new_values) return []
      const changes = []
      const newValues = audit.new_values
      const oldValues = audit.old_values || {}

      for (const key in newValues) {
        if (newValues.hasOwnProperty(key)) {
          changes.push({
            key,
            oldValue: this.formatValue(oldValues[key] ?? 'N/A'),
            newValue: this.formatValue(newValues[key]),
          })
        }
      }
      return changes
    },
    shouldShowDivider(index) {
      return index < this.auditLogs.length - 1
    },
    sortAuditLogs(a, b) {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0)
    },
    getErrorMessage(error) {
      return error?.response?.data?.message || this.$t('common.error-occurred') || 'حدث خطأ'
    },
  },
}
</script>

<style scoped>
.audit-log-button {
  margin: 0;
}

.audit-logs-container {
  padding: 1.5rem;
  max-height: 90vh;
  overflow-y: auto;
}

.audit-log-item {
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 12px;
  border-left: 6px solid #007bff;
}

.audit-log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.event-badge {
  font-size: 0.95rem;
  padding: 0.5rem 1rem;
}

.audit-date {
  font-size: 0.875rem;
  color: #6c757d;
}

.audit-log-user,
.audit-log-model {
  margin-bottom: 0.75rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
}

.audit-log-changes {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.changes-list {
  margin-top: 0.75rem;
}

.change-item {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #ffffff;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.change-field {
  font-weight: 600;
  color: #495057;
}

.change-value-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.old-value {
  color: #dc3545;
  font-weight: 500;
}

.arrow {
  font-weight: bold;
  color: #6c757d;
}

.new-value {
  color: #28a745;
  font-weight: 500;
}

.audit-logs-container::-webkit-scrollbar {
  width: 8px;
}

.audit-logs-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.audit-logs-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.audit-logs-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.audit-drawer-modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: auto;
  width: 700px;
  max-width: 95vw;
  margin: 0;
  height: 100vh;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
}

.audit-drawer-modal.show {
  transform: translateX(0);
}

.audit-drawer-modal .modal-dialog {
  margin: 0;
  height: 100%;
  max-width: 100%;
}

.audit-drawer-modal .modal-content {
  height: 100%;
  border-radius: 0;
  border: none;
  border-left: 1px solid #dee2e6;
}

.audit-drawer-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1.25rem;
  font-size: 1.25rem;
}

.audit-drawer-modal .modal-body {
  padding: 0;
  overflow-y: auto;
  height: calc(100% - 70px);
}
</style>
